import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { AlertCircle } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Illustration } from "@/components/shared-assets/illustrations";
import { EditableOrganizationRoleDetails } from "@/features/organization-roles/EditableOrganizationRoleDetails";
import { canEditOrganizationRoleDetails } from "@/features/organization-roles/organization-role-update";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { organizationQueryOptions } from "@/services/organization";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles/(roles)/$roleId/details",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles/(roles)/$roleId/details",
	});

	const {
		data: role,
		isLoading,
		isError: hasError,
	} = useQuery(
		organizationQueryOptions.systemOrganizationRoles.detail(params.roleId),
	);
	const canUpdateQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "UpdateOrganizationRole",
			entityType: "OrganizationRole",
			entityId: params.roleId,
		}),
		enabled: Boolean(params.roleId),
	});
	const canEdit = canEditOrganizationRoleDetails(canUpdateQuery.data);

	if (isLoading)
		return (
			<div className="flex items-center justify-center text-center h-[calc(100vh-16rem)]">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);

	if (hasError)
		return (
			<div className="flex flex-col items-center justify-center h-full gap-4">
				<Illustration type="cloud" size="sm">
					<div className="bg-error-500 rounded-full p-3">
						<AlertCircle className="size-6" />
					</div>
				</Illustration>
				<div className="flex flex-col items-center justify-center pt-4">
					<p className="text-gray-900 font-semibold text-md">
						{t("admin.organizations.errors.errorLoadingPage")}
					</p>
					<div className="w-[60%]">
						<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
							{t("admin.organizations.errors.errorLoadingContent")}
						</p>
					</div>
				</div>
			</div>
		);

	return (
		<div>
			{role ? (
				<EditableOrganizationRoleDetails
					organizationId={params.orgId}
					role={role}
					canEdit={canEdit}
				/>
			) : null}
		</div>
	);
}
